import React, { Suspense } from 'react';
import { graphql, PageProps } from 'gatsby';
import type { SanityPageQueryQuery } from '@repo/models/gatsby-graphql-types';
import Blocks from '@repo/ui-components/dept/organisms/Blocks/Blocks';
import createOpenGraphTags from '@repo/utils/createOpenGraphTags';
import useSiteSettingsContext from '@components/hooks/useSiteSettingsContext';
import { getLocalizedPagePath } from '@repo/gatsby-localization-helpers';
import { usePetsafeInternationalContext } from '@util/static-translations';
import Layout from '../components/layout/Layout';
import SimpleLayout from '../components/layout/SimpleLayout';

type PageContextProps = {
  _id: string;
  locale: string;
  linkHreflangValues: Array<{
    lang: string;
    url: string;
  }>;
};

function SanityPage({
  data,
  location,
  pageContext,
}: PageProps<SanityPageQueryQuery, PageContextProps>) {
  const { siteSettings } = useSiteSettingsContext();
  const { locale } = usePetsafeInternationalContext();

  const hasLTESubscriptionGrid = data.sanityPage.blocks?.blocks?.some(
    b => b?._type === 'blockBuilderSubscriptionGrid'
  );

  const LayoutComponent = hasLTESubscriptionGrid ? SimpleLayout : Layout;

  return (
    <LayoutComponent
      headTagData={{
        title: data.sanityPage.SEOMetadata?.title || data.sanityPage.title,
        description:
          data.sanityPage.SEOMetadata?.description ||
          data.sanityPage.description,
        meta: createOpenGraphTags(
          {
            title:
              data.sanityPage.SEOMetadata?.ogTitle ||
              data.sanityPage.SEOMetadata?.title ||
              data.sanityPage.title,
            description:
              data.sanityPage.SEOMetadata?.ogDescription ||
              data.sanityPage.SEOMetadata?.description ||
              data.sanityPage.description,
            url: getLocalizedPagePath(
              data.sanityPage.SEOMetadata?.ogUrl || location.pathname || '/',
              locale
            ),
            image:
              data.sanityPage.SEOMetadata?.ogImage || data.sanityPage.image,
          },
          'petsafe'
        ),
        noIndex: data.sanityPage.SEOMetadata?.noIndex || false,
        noFollow: data.sanityPage.SEOMetadata?.noFollow || false,
        includeBVScript: true,
        enableMavenoid: !hasLTESubscriptionGrid,
        enableKlaviyo: !hasLTESubscriptionGrid,
      }}
      linkHreflangValues={pageContext.linkHreflangValues}
    >
      <Suspense fallback={null}>
        {data.sanityPage.blocks?.blocks?.length > 0 && (
          <Blocks
            blocks={data.sanityPage.blocks.blocks}
            data={data}
            pageContext={pageContext}
            location={location}
            siteSettings={siteSettings}
          />
        )}
      </Suspense>
    </LayoutComponent>
  );
}

export const query = graphql`
  query SanityPageQuery($_id: String!) {
    sanityPage(_id: { eq: $_id }, siteIdentifier: { eq: "petsafe" }) {
      SEOMetadata {
        title
        description
        ogTitle
        ogDescription
        ogUrl
        ogImage {
          asset {
            _id
            url
          }
        }
        noIndex
        noFollow
      }
      title
      image: _rawImage(resolveReferences: { maxDepth: 3 })
      description
      blocks {
        ...DeptBlockFields
      }
    }
  }
`;

export default SanityPage;
